/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ReactNode, type Ref, useEffect, useRef } from 'react';

import { cssMap, jsx, type XCSSProp } from '@compiled/react';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';

// Placing this const below the function breaks Compiled!
const styles = cssMap({
	root: {
		margin: '0',
		padding: '0',
		/**
		 * The CSS reset has higher precedence, and unfortunately there is no
		 * good / UI Styling Standard compliant way to override it.
		 *
		 * A 'correct' solution would be using `:where()` pseudos in the global
		 * reset so that reset styles have 0 specificity, and will never override
		 * styles that have been explicitly set.
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		marginTop: '0 !important',
	},
});

export type ListProps = {
	children: ReactNode;
	xcss?: XCSSProp<
		| 'alignItems'
		| 'display'
		| 'gap'
		| 'paddingInlineStart'
		/**
		 * We are allowing these with expectation they are only set to '0px'
		 * to workaround Compiled ordering issues. Logical properties should be
		 * used for actual values.
		 *
		 * This component is not exposed externally so we shouldn't need to worry
		 * about misuse outside of the package.
		 */
		| 'paddingLeft'
		| 'paddingRight',
		never
	>;
};

function _List({ children, xcss }: ListProps, forwardedRef: Ref<HTMLDivElement>) {
	const internalRef = useRef<HTMLUListElement>(null);

	useEffect(() => {
		if (process.env.NODE_ENV !== 'production') {
			if (internalRef.current) {
				Array.from(internalRef.current.children).forEach((child) => {
					if (isValidChild(child)) {
						return;
					}

					// eslint-disable-next-line no-console
					console.error(
						'List Composition Error\n\n',
						'This element is not a semantically valid list item:\n\n',
						child,
						'\n\nThis is an accessibility issue and must be fixed.\n\n',
						'This message will not be displayed in production.',
					);
				});
			}
		}
	}, []);

	const ref = mergeRefs([internalRef, forwardedRef]);

	return (
		/**
		 * We are using `role="list"` instead of a `ul` element to enable more flexible
		 * composition. By using ARIA roles we can validly have elements between a list
		 * and list items, as long as those in-between elements have no semantics.
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
		<div role="list" css={styles.root} ref={ref} className={xcss}>
			{children}
		</div>
	);
}

/**
 * __List__
 *
 * An [unordered list](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/ul) with visual styles removed,
 * for semantically grouping list items.
 *
 * This is the internal primitive used by other external components in the navigation system.
 */
export const List = forwardRef<HTMLDivElement, ListProps>(_List);

/**
 * Returns true if the child is either:
 * - a list item
 * - one or more list items wrapped only in presentational elements
 */
function isValidChild(element: Element): boolean {
	/**
	 * These elements are ignored by screen readers.
	 */
	if (element.tagName === 'STYLE' || element.tagName === 'SCRIPT') {
		return true;
	}

	/**
	 * Recursive case:
	 * If this is a presentational element (no semantics) then it is valid
	 * if all of its children are valid.
	 *
	 * This check is currently much stricter than it needs to be
	 * (only allowing `div` and not allowing an explicit role).
	 *
	 * We can make it looser in the future if necessary.
	 */
	if (element.tagName === 'DIV' && element.getAttribute('role') === null) {
		return Array.from(element.children).every(isValidChild);
	}

	return element.getAttribute('role') === 'listitem';
}
